import { usePresence } from 'motion/react'
import { useEffect, useState } from 'react'
import { useKeyPressEvent, useLatest } from 'react-use'
import styled from 'styled-components'

import { type ChildrenProps, type ClassNameProps } from '@root/shared/react'

import type { DrawerControlProps } from '@root/shared/ui-kit'

export type DtDrawerProps = ClassNameProps &
  DrawerControlProps &
  ChildrenProps & {
    overlay?: boolean
    side?: 'left' | 'right'
    contentRef?: React.RefObject<HTMLDivElement | null>
  }

// TODO: Focus trap, use radix dialog?

export const DtDrawer: React.FC<DtDrawerProps> = ({
  className,
  open,
  onOpenChange,
  children,
  overlay = false,
  side = 'right',
  contentRef,
}) => {
  const presence = usePresence()
  const [isPresent, safeToRemove] = presence
  const [mounted, setMounted] = useState(false)
  const [internalOpen, setInternalOpen] = useState(false)
  const internalOpenRef = useLatest(internalOpen)
  const openRef = useLatest(open)

  useKeyPressEvent('Escape', () => onOpenChange?.(false))

  useEffect(() => {
    setInternalOpen(openRef.current)
  }, [openRef])

  useEffect(() => {
    if (open) {
      setMounted(true)
      // Let react draw the content before transition starts
      // Otherwise animation is laggy
      setTimeout(() => {
        setInternalOpen(true)
      })
    } else {
      setInternalOpen(false)
      setTimeout(() => {
        if (!internalOpenRef.current) {
          setMounted(false)
        }
      }, 200)
    }
  }, [internalOpenRef, open])

  useEffect(() => {
    // AnimatePresence signaling that drawer was remove from react tree
    if (!isPresent) {
      // Drawer was open, but was remove from react tree
      if (internalOpenRef.current) {
        setTimeout(() => {
          safeToRemove()
        }, 200)
        return
      }
      safeToRemove()
    }
  }, [internalOpenRef, isPresent, safeToRemove])

  const drawerOpen = isPresent === false ? false : internalOpen

  return (
    <>
      {overlay && mounted && (
        <Overlay
          onClick={() => {
            onOpenChange?.(false)
          }}
          $open={drawerOpen}
        />
      )}
      <Root
        data-side={side}
        aria-expanded={drawerOpen}
        className={className}
        ref={contentRef}
      >
        {mounted && <>{children}</>}
      </Root>
    </>
  )
}

const Root = styled.div`
  position: absolute;

  &[data-side='left'] {
    inset: 0 auto 0 0;
    &[aria-expanded='false'] {
      transform: translateX(-100%);
    }
    &[aria-expanded='true'] {
      transform: translateX(0);
    }
  }
  &[data-side='right'] {
    inset: 0 0 0 auto;
    &[aria-expanded='true'] {
      transform: translateX(0);
    }
    &[aria-expanded='false'] {
      transform: translateX(100%);
      padding-inline: 0;
    }
  }

  display: grid;
  min-height: 0;
  grid-template-rows: minmax(0, 1fr);
  background: var(--appearance-1);

  transition: transform 0.2s ease-in-out;
`

const Overlay = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  background-color: var(--appearanceA-6);
  transition: opacity 0.2s ease-in-out;
  opacity: ${({ $open: open }) => (open ? 1 : 0)};
`
